// this component represents Listing page body between header and footer when no error occurs on the server
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';

import { resolveByCountry } from '@codex/helpers';

import ListingPageLoadingSpinner from '../ListingPageLoadingSpinner/ListingPageLoadingSpinner';
import SegmentListingPageBody from '../SegmentListingPageBody/SegmentListingPageBody';
import SearchListingPageBody from '../SearchListingPageBody/SearchListingPageBody';
import Error500 from '../ErrorPagesComponents/Error500';

import { loadDataForSearchListingPageForFirstRenderOnClient } from '../../reduck/listingPageFirstRenderOnClient.reduck';
import { getActiveListingPage, getTitleHTMLElementValue } from '../../reduck/selectors';
import {
  checkAndGetSponsoredProducts, reFetchProductsForSearchLabsABTest,
  searchStockAvailabilityForProductsOnPageAndSetProductsAnalyticsData
} from '../../reduck/listingPagePages.reduck';
import { setPageAnalyticsInDigitalData } from '../../analytics/pageAndProductsAnalytics';
import { initLocationSearch } from '../../reduck/listingPageLocationSearchSetup.reduck';
import UISettings from '../../settings/UISettings';
import Error404 from '../ErrorPagesComponents/Error404';


const ListingPageBody = ({ listingPageDataLoadedForFirstRender, listingPageDataLoadingForFirstRender,
  listingPageDataLoadingForFirstRenderFailed, errorCodeOnClient, clientSideRendering, loadDataForFirstRenderOnClient,
  titleHTMLElementValue, getStockAvailabilityForProductsOnFirstRenderPageAndSetProductsAnalyticsData,
  initPageAnalyticsForFirstRenderedPageInDigitalData, initializeLocationSearch, getSponsoredProductsForFirstRenderedPage,
  activeListingPageAttributes, typeOfActiveListingPage, reFetchDataForSearchLabsABTest }) => {

  const isUKSite = resolveByCountry(true, false);

  // Loads data for first page when data for first render are loaded on the client side and not on server
  // Currently data for search listing page is only loaded on the client side for first render
  useEffect(() => {
    if (!listingPageDataLoadedForFirstRender) {
      // this is called only when data are not loaded for first render on client
      // so currently it's called only for search listing page
      // In some cases, It can redirects to a different URL based on gathered data (SIDE EFFECT!)
      loadDataForFirstRenderOnClient();
    }

  }, [listingPageDataLoadedForFirstRender]);

  // THIS EFFECT IS JUST USED FOR SEARCHLABS A/B TEST:
  // When a searchLabs a/b test is active the products data has to refetched on client
  // after first render and first loading of the page.
  // SearchLabs a/b test is active when global window.Search_VisitorSegmentId variable is defined and has a string value
  // TODO: Findability team has to change architecture of this a/b test because it has very low architecture
  // TODO: Remove this code as soon as Findability changes the architecture of the a/b test
  useEffect(() => {
    if (listingPageDataLoadedForFirstRender && !listingPageDataLoadingForFirstRenderFailed) {
      // event listener for browser load event
      window.addEventListener('load', async (event) => {
        if (window.Search_VisitorSegmentId && typeOfActiveListingPage === 'segmentListingPageRenderedOnServer') {
          // this 'if' branch is performed only once
          // when search Labs a/b test is turned on and first render performed on server
          // Products data has to be refetched for searchLabs a/b test on client
          // Currently it's called only for segment listing page
          // In some cases, It can redirects to a different URL based on gathered data (SIDE EFFECT!)
          await reFetchDataForSearchLabsABTest();
        }
      });

      // event listener for 'Search_VisitorSegmentId_CHANGED' custom event which can be also used by a/b test
      window.addEventListener('Search_VisitorSegmentId_CHANGED', async (event) => {
        if (window.Search_VisitorSegmentId && typeOfActiveListingPage === 'segmentListingPageRenderedOnServer') {
          // this 'if' branch is performed only once
          // when search Labs a/b test is turned on and first render performed on server
          // Products data has to be refetched for searchLabs a/b test on client
          // Currently it's called only for segment listing page
          // In some cases, It can redirects to a different URL based on gathered data (SIDE EFFECT!)
          await reFetchDataForSearchLabsABTest();
        }
      });
    }

  }, [listingPageDataLoadedForFirstRender, listingPageDataLoadingForFirstRenderFailed]);

  // This inits browser specific functionality for body of the listing page
  useEffect(() => {
    // This if branch is called in one of these 2 cases and only once:
    // - after hydration for segment listing page finished
    // - after first render on client for search listing page
    if (listingPageDataLoadedForFirstRender && !listingPageDataLoadingForFirstRenderFailed) {

      // changes history state for the first page loaded in the web browser
      // when user gets back to the first page by clicking on back button in the web browser,
      // the page is loaded using AJAX calls instead of reloading of the whole page
      window.history.replaceState(
        {
          nextPageAttributes: activeListingPageAttributes
        },
        '',
        window.location.href
      );

      // search default stock availability because it's not done on server
      getStockAvailabilityForProductsOnFirstRenderPageAndSetProductsAnalyticsData();

      // inits analytics is only in browser
      initPageAnalyticsForFirstRenderedPageInDigitalData();


      const isLocationSearchEnabled = UISettings.get('component.mcd.postcodechecker.enabled_for_this_segment') !== false &&
        UISettings.get('component.mcd.postcodechecker.enabled') !== false;
      if (isLocationSearchEnabled) {
        // init location search if it is enabled
        initializeLocationSearch();
      }

      if (typeOfActiveListingPage === 'segmentListingPageRenderedOnServer') {
        // sponsored products are rendered only in browser and
        // they're rendered only for segment listing page
        // so we init them after rendering on server
        getSponsoredProductsForFirstRenderedPage();

        if (isUKSite) {
          // Google ads init
          // It is only used for Currys UK and it's used only for segment listing page
          window.googletag.cmd.push(() => { window.googletag.display('div-gpt-ad-wideskytop-1'); });
          window.googletag.cmd.push(() => { window.googletag.display('div-gpt-ad-wideskybot-2'); });
          window.googletag.cmd.push(() => { window.googletag.display('div-gpt-ad-leadertop-0'); });
        }
      }
    }
  }, [listingPageDataLoadedForFirstRender, listingPageDataLoadingForFirstRenderFailed]);


  return (
    <>
      {/* Changing content of <title> on client using react-helmet */}
      {

        clientSideRendering && titleHTMLElementValue && (
          <Helmet>
            <title>
              {titleHTMLElementValue}
            </title>
          </Helmet>
        )
      }
      {/* Data for first render are loading so spinner is shown */}
      {listingPageDataLoadingForFirstRender && <ListingPageLoadingSpinner />}

      {/* loading data for first render has failed so Error page is shown */}
      {listingPageDataLoadingForFirstRenderFailed && (errorCodeOnClient === 404 ? <Error404 /> : <Error500 />)}

      {/* Listing page data for first render has been loaded so we can show currently active segment or search listing page */}
      {listingPageDataLoadedForFirstRender && (typeOfActiveListingPage === 'segmentListingPage' ||
        typeOfActiveListingPage === 'segmentListingPageRenderedOnServer') && (
        <SegmentListingPageBody />
      )}
      {listingPageDataLoadedForFirstRender && typeOfActiveListingPage === 'searchListingPage' && (
        <SearchListingPageBody />
      )}
    </>
  );
};

ListingPageBody.displayName = 'ListingPageBody';

ListingPageBody.propTypes = {
  listingPageDataLoadedForFirstRender: PropTypes.bool.isRequired,
  listingPageDataLoadingForFirstRender: PropTypes.bool.isRequired,
  listingPageDataLoadingForFirstRenderFailed: PropTypes.bool.isRequired,
  clientSideRendering: PropTypes.bool.isRequired,
  loadDataForFirstRenderOnClient: PropTypes.func.isRequired,
  getStockAvailabilityForProductsOnFirstRenderPageAndSetProductsAnalyticsData: PropTypes.func.isRequired,
  initPageAnalyticsForFirstRenderedPageInDigitalData: PropTypes.func.isRequired,
  initializeLocationSearch: PropTypes.func.isRequired,
  getSponsoredProductsForFirstRenderedPage: PropTypes.func.isRequired,
  reFetchDataForSearchLabsABTest: PropTypes.func.isRequired,
  errorCodeOnClient: PropTypes.oneOf([500, 404]),
  titleHTMLElementValue: PropTypes.string,
  activeListingPageAttributes: PropTypes.shape({}),
  typeOfActiveListingPage:
    PropTypes.oneOf(['segmentListingPage', 'searchListingPage', 'segmentListingPageRenderedOnServer'])

};

ListingPageBody.defaultProps = {
  titleHTMLElementValue: undefined,
  activeListingPageAttributes: undefined,
  typeOfActiveListingPage: undefined,
  errorCodeOnClient: undefined
};

const mapStateToProps = (state) => {
  const activeListingPage = getActiveListingPage(state);

  let typeOfActiveListingPage;
  if (activeListingPage && activeListingPage.searchListingPage) {
    typeOfActiveListingPage = 'searchListingPage';
  }

  if (activeListingPage && !activeListingPage.searchListingPage) {
    if (activeListingPage.renderedOnServer) {
      typeOfActiveListingPage = 'segmentListingPageRenderedOnServer';
    } else {
      typeOfActiveListingPage = 'segmentListingPage';
    }
  }

  return {
    listingPageDataLoadedForFirstRender: !!activeListingPage,
    listingPageDataLoadingForFirstRender: state.listingPage.firstRenderOnClient.listingPageDataLoadingForFirstRender,
    listingPageDataLoadingForFirstRenderFailed: state.listingPage.firstRenderOnClient.listingPageDataLoadingForFirstRenderFailed,
    errorCodeOnClient: state.listingPage.firstRenderOnClient.errorCodeOnClient,
    clientSideRendering: typeof state.listingPage.browser?.device !== 'undefined',
    titleHTMLElementValue: activeListingPage ? getTitleHTMLElementValue(state) : undefined,
    activeListingPageAttributes: activeListingPage ? activeListingPage.pageAttributes : undefined,
    typeOfActiveListingPage: typeOfActiveListingPage
  };
};

const mapDispatchToProps = (dispatch) => ({
  loadDataForFirstRenderOnClient: async () => {
    // currently we are loading data for first render on client only for search listing page
    // if we want to load data for segment listing page on client, this has to be extended in the future
    dispatch(loadDataForSearchListingPageForFirstRenderOnClient());
  },
  getStockAvailabilityForProductsOnFirstRenderPageAndSetProductsAnalyticsData: async () => {
    dispatch(searchStockAvailabilityForProductsOnPageAndSetProductsAnalyticsData());
  },
  initPageAnalyticsForFirstRenderedPageInDigitalData: () => {
    dispatch(setPageAnalyticsInDigitalData(false, false, true));
  },
  initializeLocationSearch: () => {
    dispatch(initLocationSearch());
  },
  getSponsoredProductsForFirstRenderedPage: async () => {
    dispatch(checkAndGetSponsoredProducts());
  },
  reFetchDataForSearchLabsABTest: () => {
    dispatch(reFetchProductsForSearchLabsABTest());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ListingPageBody);
